import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToastContext, Button as BlueprintButton } from '@resideo/blueprint-oneportal';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styles from './Employees.module.css';
import apiService, { type ConsumerUser, type ResendInviteRequest, type ConsumerFilterBy } from '@/services/apiService';
import { ResendConfirmModal } from './ResendConfirmModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { useAuth } from '@/contexts/AuthContext';
import { EmployeeFilterPanel, type FilterState, type LocationOption } from './EmployeeFilterPanel';
import { usePermissions } from '@/hooks/usePermissions';
import { PermissionKey } from '@/services/permissionService';
import { useConsumerUsers } from '@/hooks/useConsumerUsers';
import { getRoleLabel } from './roleUtils';

/** Extract the display name — falls back to the email from the most recent invite */
function displayName(cu: ConsumerUser): string {
  const first = cu.user?.firstName?.trim() ?? '';
  const last = cu.user?.lastName?.trim() ?? '';
  const full = [first, last].filter(Boolean).join(' ');
  if (full) return full;
  return latestInviteEmail(cu) ?? '-';
}

/** Contact email — falls back to invite email */
function displayEmail(cu: ConsumerUser): string {
  if (cu.user?.contactEmail) return cu.user.contactEmail;
  return latestInviteEmail(cu) ?? '-';
}

function latestInviteEmail(cu: ConsumerUser): string | undefined {
  if (!cu.invites?.length) return undefined;
  return cu.invites[cu.invites.length - 1].sendToEmail;
}

/** A user is active if they were never invited (account owner/creator)
 *  or they have at least one ACCEPTED invite. Role is irrelevant. */
function isActive(cu: ConsumerUser): boolean {
  const invites = cu.invites ?? [];
  return invites.length === 0 || invites.some(i => i.status === 'ACCEPTED');
}

interface InvitedRow {
  inviteId: string;
  consumerUserId: string;
  name: string;
  email: string;
  role: string;
  expiresAt?: string | null;
}

/** Resolve display name for a pending invite row.
 *  Priority: user.firstName+lastName → invite.firstName+lastName → invite.name → '—' */
function inviteDisplayName(cu: ConsumerUser, invite: { firstName?: string | null; lastName?: string | null; name?: string | null }): string {
  const first = cu.user?.firstName?.trim() ?? '';
  const last  = cu.user?.lastName?.trim() ?? '';
  const full  = [first, last].filter(Boolean).join(' ');
  if (full) return full;
  // Use firstName/lastName directly from the invite (returned by v2 endpoint for PENDING users)
  const invFirst = invite.firstName?.trim() ?? '';
  const invLast  = invite.lastName?.trim() ?? '';
  const invFull  = [invFirst, invLast].filter(Boolean).join(' ');
  if (invFull) return invFull;
  // The API sometimes stores name as "firstName, lastName" — strip the comma.
  return invite.name?.replace(/,/g, '').replace(/\s+/g, ' ').trim() || '—';
}

/** Flatten all non-accepted invites across all users into display rows */
function getInvitedRows(users: ConsumerUser[]): InvitedRow[] {
  const rows: InvitedRow[] = [];
  for (const cu of users) {
    for (const invite of cu.invites ?? []) {
      if (invite.status !== 'ACCEPTED') {
        rows.push({
          inviteId: invite.id,
          consumerUserId: cu.id,
          name: inviteDisplayName(cu, invite),
          email: invite.sendToEmail,
          role: cu.role,
          expiresAt: invite.expiresAt,
        });
      }
    }
  }
  return rows;
}

function formatExpiry(expiresAt?: string | null): string {
  if (!expiresAt) return '—';
  try {
    const expiry = new Date(expiresAt);
    if (expiry < new Date()) return 'Expired';
    return expiry.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

const FilterToolbarIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M10.0859 2.49219C11.1547 2.49219 12.0696 3.16592 12.4142 4.11735H16.6655V5.78402H12.4142C12.0696 6.73565 11.1547 7.40999 10.0859 7.40999C9.01735 7.40981 8.10287 6.73554 7.75846 5.78402H3.49089V4.11735H7.75846C8.10289 3.16602 9.01738 2.49237 10.0859 2.49219ZM10.0859 4.15885C9.62936 4.15908 9.27715 4.52006 9.27702 4.95068C9.27707 5.38137 9.6293 5.7431 10.0859 5.74333C10.5428 5.74333 10.8956 5.38151 10.8957 4.95068C10.8955 4.51992 10.5427 4.15885 10.0859 4.15885Z" fill="currentColor"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M15.0218 7.54158C16.3826 7.54158 17.4981 8.63238 17.4982 10.0001C17.4982 11.3679 16.3826 12.4594 15.0218 12.4594C13.9534 12.4591 13.0396 11.7848 12.6952 10.8334H3.49108V9.16674H12.6952C13.0397 8.21563 13.9535 7.54184 15.0218 7.54158ZM15.0218 9.20825C14.5654 9.20858 14.2131 9.56951 14.2129 10.0001C14.2129 10.4307 14.5653 10.7924 15.0218 10.7927C15.4787 10.7927 15.8316 10.4309 15.8316 10.0001C15.8314 9.56932 15.4786 9.20825 15.0218 9.20825Z" fill="currentColor"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M4.9834 12.5907C6.0521 12.5907 6.96629 13.2652 7.31087 14.2166H16.6647V15.8833H7.31169C6.96705 16.8348 6.05204 17.5092 4.9834 17.5093C3.62288 17.5091 2.50803 16.4175 2.50781 15.05C2.50795 13.6824 3.62283 12.5908 4.9834 12.5907ZM4.9834 14.2581C4.52677 14.2583 4.17462 14.6193 4.17448 15.05C4.1747 15.4806 4.52682 15.8424 4.9834 15.8426C5.44008 15.8426 5.79291 15.4806 5.79313 15.05C5.793 14.6192 5.44014 14.2582 4.9834 14.2581Z" fill="currentColor"/>
  </svg>
);

type Tab = 'active' | 'invited';

export function Employees() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') === 'invited' ? 'invited' : 'active';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteSearchQuery, setInviteSearchQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top?: number; bottom?: number; right: number }>({ top: 0, right: 0 });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [pendingResendId, setPendingResendId] = useState<string | null>(null);
  const [pendingDeleteRow, setPendingDeleteRow] = useState<{ consumerUserId: string; email: string; isActive: boolean } | null>(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>({ roles: new Set(), locationIds: new Set() });

  const { addToast } = useToastContext();
  const queryClient = useQueryClient();
  const { user: authUser, locations: contextLocations, selectedLocation } = useAuth();
  const selfEmail = authUser?.email?.toLowerCase() ?? null;

  // ─── Derive consumerAccountId and availableLocations from AuthContext ─────────
  // AuthContext already fetches accounts during session restore — no extra API
  // call is needed here. This also prevents React StrictMode from double-firing
  // the effect in development and making two identical network requests.

  const consumerAccountId: string | null =
    selectedLocation?.consumerAccountId ??
    contextLocations[0]?.consumerAccountId ??
    null;

  const availableLocations: LocationOption[] = useMemo(
    () => contextLocations.map(loc => ({ id: loc.id, name: loc.name })),
    [contextLocations],
  );

  // ─── Build server-side filter from filter panel state ───────────────────────

  const activeFilterBy = useMemo<ConsumerFilterBy | undefined>(() => {
    if (filterState.roles.size === 0 && filterState.locationIds.size === 0) {
      return undefined;
    }
    return {
      roles: [...filterState.roles].join(','),
      locationIds: [...filterState.locationIds].join(','),
    };
  }, [filterState]);

  // ─── Phase 2: load users (re-fetches whenever account or filter changes) ─────

  const {
    users: allUsers,
    isLoading: usersLoading,
    error: usersError,
  } = useConsumerUsers(consumerAccountId, activeFilterBy);

  const isLoading = usersLoading;
  const error = usersError;

  // ─── Close dropdown on outside click ──────────────────────────────────────

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    }
    if (openMenuId) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenuId]);

  // ─── Delete handler ───────────────────────────────────────────────────────

  async function handleDelete(cuId: string) {
    if (!consumerAccountId) return;
    setDeletingId(cuId);
    setDeleteError(null);
    try {
      await apiService.deleteConsumerUser(consumerAccountId, cuId);
      addToast({ message: t('employees.accountDeleted'), toastType: 'Success', zIndex: '9999' });
      // Optimistically remove the deleted user from the React Query cache so the
      // UI updates immediately without waiting for a background refetch.
      const filterKey = activeFilterBy ? JSON.stringify(activeFilterBy) : '';
      queryClient.setQueryData(
        ['consumerUsers', consumerAccountId, filterKey],
        (old: ConsumerUser[] | undefined) => (old ?? []).filter(u => u.id !== cuId),
      );
    } catch {
      setDeleteError('Failed to delete. Please try again.');
    } finally {
      setDeletingId(null);
      setPendingDeleteRow(null);
    }
  }

  async function handleResend(inviteId: string) {
    if (!consumerAccountId) return;
    setResendingId(inviteId);
    setResendError(null);
    try {
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      const body: ResendInviteRequest = {
        templateLanguage: 'en',
        baseUrl: `${import.meta.env.VITE_RESIDEO_ID_URL}/mobile`,
        expiresAt,
      };
      await apiService.resendInvite(consumerAccountId, inviteId, body);
      addToast({ message: t('employees.invitationResent'), toastType: 'Success', zIndex: '9999' });
    } catch {
      setResendError('Failed to resend. Please try again.');
    } finally {
      setResendingId(null);
      setPendingResendId(null);
    }
  }

  // ─── Derived data ─────────────────────────────────────────────────────────────

  const employees = allUsers.filter(isActive);
  const invitedRows = getInvitedRows(allUsers);

  // ─── Search filter ────────────────────────────────────────────────────────────────

  const query = searchQuery.trim().toLowerCase();
  const filtered = employees.filter(cu => {
    if (query && !displayName(cu).toLowerCase().includes(query) && !displayEmail(cu).toLowerCase().includes(query)) {
      return false;
    }
    return true;
  });

  const activeFilterCount = filterState.roles.size + filterState.locationIds.size;

  const inviteQuery = inviteSearchQuery.trim().toLowerCase();
  const filteredInvited = invitedRows.filter(r => {
    if (inviteQuery && !r.email.toLowerCase().includes(inviteQuery) && !r.name.toLowerCase().includes(inviteQuery)) {
      return false;
    }
    return true;
  });

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>{t('employees.title')}</h1>
          <div className={styles.tabs}>
            <div className={styles.tabItem}>
              <button
                type="button"
                className={`${styles.tab} ${activeTab === 'active' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('active')}
              >
                {t('employees.active')}
              </button>
              <div className={`${styles.tabBar} ${activeTab === 'active' ? styles.tabBarActive : ''}`} />
            </div>
            <div className={styles.tabItem}>
              <button
                type="button"
                className={`${styles.tab} ${activeTab === 'invited' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('invited')}
              >
                {t('employees.invited')}
              </button>
              <div className={`${styles.tabBar} ${activeTab === 'invited' ? styles.tabBarActive : ''}`} />
            </div>
          </div>
        </div>
        <BlueprintButton className={styles.inviteButton} onClick={() => navigate('/employees/invite')}>
          {t('employees.inviteEmployee')}
        </BlueprintButton>
      </div>

      {/* Tab content */}
      {activeTab === 'invited' ? (
        <>
          <div className={styles.tabMeta}>
            {/* Title row */}
            <div className={styles.contentHeader}>
              <h2 className={styles.contentTitle}>{t('employees.invitedEmployees')}</h2>
            </div>
            {/* Search + Filters */}
            <div className={styles.toolbar}>
            <div className={styles.searchBar}>
              <div className={styles.searchInput}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="7.5" cy="7.5" r="5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M11.5 11.5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  placeholder={t('employees.search')}
                  value={inviteSearchQuery}
                  onChange={e => setInviteSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <BlueprintButton
              variant="secondary"
              className={`${styles.filterButton} ${activeFilterCount > 0 ? styles.filterSelected : ''}`}
              onClick={() => setIsFilterPanelOpen(true)}
            >
              <FilterToolbarIcon className={styles.filterIcon} />
              <span>Filters</span>
              {activeFilterCount > 0 && <span className={styles.filterBadge}>{activeFilterCount}</span>}
            </BlueprintButton>
            </div>
          </div>
          <div className={styles.content}>
          {/* Delete error banner */}
          {deleteError && (
            <div className={styles.stateContainer}>
              <span className={styles.errorText}>{deleteError}</span>
            </div>
          )}
          {/* Resend error banner */}
          {resendError && (
            <div className={styles.stateContainer}>
              <span className={styles.errorText}>{resendError}</span>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <colgroup>
                  <col className={styles.colCheckbox} />
                  <col className={styles.colName} />
                  <col className={styles.colEmail} />
                  <col className={styles.colRole} />
                  <col className={styles.colExpires} />
                  <col className={styles.colSpacer} />
                  <col className={styles.colActions} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={styles.checkboxCol}></th>
                    <th>{t('employees.name')}</th>
                    <th>{t('employees.email')}</th>
                    <th>{t('employees.role')}</th>
                    <th>{t('employees.invitationExpires')}</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5].map(i => (
                    <tr key={i}>
                      <td><div className={styles.loadingSkeleton} style={{ width: 16, height: 16 }} /></td>
                      <td><div className={styles.loadingSkeleton} style={{ width: '60%' }} /></td>
                      <td><div className={styles.loadingSkeleton} style={{ width: '70%' }} /></td>
                      <td><div className={styles.loadingSkeleton} style={{ width: '40%' }} /></td>
                      <td><div className={styles.loadingSkeleton} style={{ width: '50%' }} /></td>
                      <td></td>
                      <td></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Error */}
          {!isLoading && error && (
            <div className={styles.stateContainer}>
              <span className={styles.errorText}>{error}</span>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && filteredInvited.length === 0 && (
            <div className={styles.stateContainer}>
              <span>
                {inviteQuery || activeFilterCount > 0 ? t('employees.noInvitationsMatch') : t('employees.noPendingInvitations')}
              </span>
            </div>
          )}

          {/* Table */}
          {!isLoading && !error && filteredInvited.length > 0 && (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <colgroup>
                  <col className={styles.colCheckbox} />
                  <col className={styles.colName} />
                  <col className={styles.colEmail} />
                  <col className={styles.colRole} />
                  <col className={styles.colExpires} />
                  <col className={styles.colSpacer} />
                  <col className={styles.colActions} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={styles.checkboxCol}>
                      <input type="checkbox" disabled title="Select all" />
                    </th>
                    <th>{t('employees.name')}</th>
                    <th>{t('employees.email')}</th>
                    <th>{t('employees.role')}</th>
                    <th>{t('employees.invitationExpires')}</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvited.map(row => (
                    <tr key={row.inviteId}>
                      <td className={styles.checkboxCol}>
                        <input type="checkbox" disabled />
                      </td>
                      <td><span className={styles.nameLink} style={{ cursor: 'default', textDecoration: 'none' }}>{row.name}</span></td>
                      <td>{row.email}</td>
                      <td>
                        <span className={styles.roleBadge}>{getRoleLabel(row.role, t)}</span>
                      </td>
                      <td>{formatExpiry(row.expiresAt)}</td>
                      <td></td>
                      <td className={styles.actionsCol}>
                        <div ref={openMenuId === row.inviteId ? menuRef : null}>
                          <button
                            className={styles.moreBtn}
                            title="More options"
                            onClick={(e) => {
                              if (openMenuId === row.inviteId) {
                                setOpenMenuId(null);
                              } else {
                                const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                                const spaceBelow = window.innerHeight - rect.bottom;
                                if (spaceBelow < 120) {
                                  setMenuPos({ bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right });
                                } else {
                                  setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                }
                                setOpenMenuId(row.inviteId);
                              }
                            }}
                            aria-haspopup="true"
                            aria-expanded={openMenuId === row.inviteId}
                            aria-label="More options"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" fill="#605B51"/>
                              <path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" fill="#605B51"/>
                              <path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" fill="#605B51"/>
                              <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" stroke="#605B51" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" stroke="#605B51" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" stroke="#605B51" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          {openMenuId === row.inviteId && (
                            <div
                              className={styles.dropdownMenu}
                              style={{
                                top: menuPos.top,
                                bottom: menuPos.bottom,
                                right: menuPos.right,
                              }}
                            >
                              <BlueprintButton
                                variant="text"
                                className={styles.dropdownItem}
                                disabled={resendingId === row.inviteId}
                                onClick={() => {
                                  setOpenMenuId(null);
                                  setPendingResendId(row.inviteId);
                                }}
                              >
                                {t('employees.resend')}
                              </BlueprintButton>
                              <BlueprintButton
                                variant="text"
                                className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                                disabled={row.email.toLowerCase() === (selfEmail ?? '') || deletingId === row.consumerUserId}
                                onClick={() => {
                                  setOpenMenuId(null);
                                  setPendingDeleteRow({ consumerUserId: row.consumerUserId, email: row.email, isActive: false });
                                }}
                              >
                                {t('employees.delete')}
                              </BlueprintButton>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Resend confirmation modal */}
          {pendingResendId && (
            <ResendConfirmModal
              isResending={resendingId === pendingResendId}
              onConfirm={() => handleResend(pendingResendId)}
              onCancel={() => setPendingResendId(null)}
            />
          )}

        </div>
        </>
      ) : (
        <>
          <div className={styles.tabMeta}>
            {/* Title row */}
            <div className={styles.contentHeader}>
              <h2 className={styles.contentTitle}>{t('employees.activeEmployees')}</h2>
            </div>
            {/* Search + Filters */}
            <div className={styles.toolbar}>
            {/* Search */}
            <div className={styles.searchBar}>
              <div className={styles.searchInput}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M6.66667 1.66667C3.90524 1.66667 1.66667 3.90524 1.66667 6.66667C1.66667 9.42809 3.90524 11.6667 6.66667 11.6667C9.42809 11.6667 11.6667 9.42809 11.6667 6.66667C11.6667 3.90524 9.42809 1.66667 6.66667 1.66667ZM0 6.66667C0 2.98477 2.98477 0 6.66667 0C10.3486 0 13.3333 2.98477 13.3333 6.66667C13.3333 10.3486 10.3486 13.3333 6.66667 13.3333C2.98477 13.3333 0 10.3486 0 6.66667Z" fill="#56648B"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M10.2441 10.2441C10.5695 9.91864 11.0972 9.91864 11.4226 10.2441L14.7559 13.5774C15.0814 13.9028 15.0814 14.4305 14.7559 14.7559C14.4305 15.0814 13.9028 15.0814 13.5774 14.7559L10.2441 11.4226C9.91864 11.0972 9.91864 10.5695 10.2441 10.2441Z" fill="#56648B"/>
                </svg>
                <input
                  type="text"
                  placeholder={t('employees.search')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Filters button */}
            <BlueprintButton
              variant="secondary"
              className={`${styles.filterButton} ${activeFilterCount > 0 ? styles.filterSelected : ''}`}
              onClick={() => setIsFilterPanelOpen(true)}
            >
              <FilterToolbarIcon className={styles.filterIcon} />
              <span>Filters</span>
              {activeFilterCount > 0 && <span className={styles.filterBadge}>{activeFilterCount}</span>}
            </BlueprintButton>
            </div>
          </div>
          <div className={styles.content}>

          {/* Loading */}
          {isLoading && (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <colgroup>
                  <col className={styles.colCheckbox} />
                  <col className={styles.colName} />
                  <col className={styles.colEmail} />
                  <col className={styles.colRole} />
                  <col className={styles.colSpacer} />
                  <col className={styles.colActions} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={styles.checkboxCol}></th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th></th>
                    <th className={styles.actionsCol}></th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5].map(i => (
                    <tr key={i}>
                      <td><div className={styles.loadingSkeleton} style={{ width: 16, height: 16 }} /></td>
                      <td><div className={styles.loadingSkeleton} style={{ width: '60%' }} /></td>
                      <td><div className={styles.loadingSkeleton} style={{ width: '70%' }} /></td>
                      <td><div className={styles.loadingSkeleton} style={{ width: '40%' }} /></td>
                      <td></td>
                      <td></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Error */}
          {!isLoading && error && (
            <div className={styles.stateContainer}>
              <span className={styles.errorText}>{error}</span>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && filtered.length === 0 && (
            <div className={styles.stateContainer}>
              <span>
                {query || activeFilterCount > 0 ? 'No employees match your search or filters.' : 'No active employees found.'}
              </span>
            </div>
          )}

          {/* Table */}
          {!isLoading && !error && filtered.length > 0 && (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <colgroup>
                  <col className={styles.colCheckbox} />
                  <col className={styles.colName} />
                  <col className={styles.colEmail} />
                  <col className={styles.colRole} />
                  <col className={styles.colSpacer} />
                  <col className={styles.colActions} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={styles.checkboxCol}>
                      <input type="checkbox" disabled title="Select all" />
                    </th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th></th>
                    <th className={styles.actionsCol}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(cu => {
                    const isSelf = !!selfEmail && displayEmail(cu).toLowerCase() === selfEmail;
                    return (
                    <tr key={cu.id}>
                      <td className={styles.checkboxCol}>
                        <input type="checkbox" disabled />
                      </td>
                      <td>
                        {isSelf ? (
                          <span className={styles.nameLink} style={{ cursor: 'default', textDecoration: 'none' }}>
                            {displayName(cu)} (You)
                          </span>
                        ) : (
                          <button
                            className={styles.nameLink}
                            onClick={() => navigate(`/employees/${cu.id}`, { state: { user: cu } })}
                          >
                            {displayName(cu)}
                          </button>
                        )}
                      </td>
                      <td>{displayEmail(cu)}</td>
                      <td>
                        <span className={styles.roleBadge}>{getRoleLabel(cu.role, t)}</span>
                      </td>
                      <td></td>
                      <td className={styles.actionsCol}>
                        {!isSelf && (
                          <div ref={openMenuId === cu.id ? menuRef : null}>
                            <button
                              className={styles.moreBtn}
                              title="More options"
                              onClick={(e) => {
                                if (openMenuId === cu.id) {
                                  setOpenMenuId(null);
                                } else {
                                  const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                                  const spaceBelow = window.innerHeight - rect.bottom;
                                  if (spaceBelow < 120) {
                                    setMenuPos({ bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right });
                                  } else {
                                    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                  }
                                  setOpenMenuId(cu.id);
                                }
                              }}
                              aria-haspopup="true"
                              aria-expanded={openMenuId === cu.id}
                              aria-label="More options"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" fill="#605B51"/>
                                <path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" fill="#605B51"/>
                                <path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" fill="#605B51"/>
                                <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" stroke="#605B51" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" stroke="#605B51" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" stroke="#605B51" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            {openMenuId === cu.id && (
                              <div
                                className={styles.dropdownMenu}
                                style={{
                                  top: menuPos.top,
                                  bottom: menuPos.bottom,
                                  right: menuPos.right,
                                }}
                              >
                                <BlueprintButton
                                  variant="text"
                                  className={styles.dropdownItem}
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    navigate(`/employees/${cu.id}`, { state: { user: cu } });
                                  }}
                                >
                                  {t('employees.view')}
                                </BlueprintButton>
                                {hasPermission(PermissionKey.USER_DELETE) && (
                                  <BlueprintButton
                                    variant="text"
                                    className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setPendingDeleteRow({ consumerUserId: cu.id, email: displayEmail(cu), isActive: true });
                                    }}
                                  >
                                    {t('employees.delete')}
                                  </BlueprintButton>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
      )}

      {/* Filter panel */}
      <EmployeeFilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        onApply={setFilterState}
        currentFilters={filterState}
        availableLocations={availableLocations}
      />

      {/* Delete confirmation modal — shared between Active and Invited tabs */}
      {pendingDeleteRow && (
        <DeleteConfirmModal
          email={pendingDeleteRow.email}
          isActive={pendingDeleteRow.isActive}
          isDeleting={deletingId === pendingDeleteRow.consumerUserId}
          onConfirm={() => handleDelete(pendingDeleteRow.consumerUserId)}
          onCancel={() => setPendingDeleteRow(null)}
        />
      )}
    </div>
  );
}
